// regional-hub-analytics.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of } from 'rxjs';
import { environment } from 'src/environments/environment';

// ========================================
// INTERFACES
// ========================================

export interface RegionalHubOverview {
  regionalHub: {
    id: number;
    name: string;
    city: string;
    type: string;
  };
  period: string;
  overview: {
    businessHubs: number;
    activeUsers: number;
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    totalPosts: number;
    smartCoinsEarned: number;
    smartCoinsRedeemed: number;
    smartCoinsCirculation: number;
  };
}

export interface OrderAnalytics {
  period: string;
  analytics: {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    uniqueCustomers: number;
    revenueTrend: number;
    ordersTrend: number;
  };
  statusBreakdown: Array<{
    status: string;
    count: number;
  }>;
  topBusinesses: Array<{
    id: number;
    name: string;
    avatar: string;
    orderCount: number;
    revenue: number;
  }>;
}

export interface OrderMetrics {
  period: string;
  granularity: string;
  revenue: Array<{
    date: string;
    value: number;
  }>;
  orders: Array<{
    date: string;
    value: number;
  }>;
}

export interface UserAnalytics {
  period: string;
  analytics: {
    totalUsers: number;
    newUsers: number;
    activeUsers: number;
    growthTrend: number;
  };
  levelDistribution: Array<{
    level: number;
    count: number;
  }>;
}

export interface UserGrowth {
  period: string;
  growthChart: Array<{
    date: string;
    newUsers: number;
  }>;
}

export interface PostAnalytics {
  period: string;
  analytics: {
    totalPosts: number;
    businessPosts: number;
    regionalPosts: number;
    userPosts: number;
    totalLikes: number;
    totalComments: number;
    totalViews: number;
    engagementRate: number;
  };
  postTypeBreakdown: Array<{
    type: string;
    count: number;
  }>;
  topPosts: Array<{
    id: number;
    content: string;
    images: string[];
    createdAt: string;
    author: string;
    likes: number;
    comments: number;
    engagement: number;
  }>;
}

export interface PostEngagement {
  period: string;
  engagementChart: Array<{
    date: string;
    posts: number;
    likes: number;
    comments: number;
    totalEngagement: number;
  }>;
}

export interface SmartCoinOverview {
  period: string;
  overview: {
    totalEarned: number;
    totalEarnedEuros: number;
    totalRedeemed: number;
    totalRedeemedEuros: number;
    netFlow: number;
    netFlowEuros: number;
    totalCirculation: number;
    totalCirculationEuros: number;
    activeUsers: number;
    earnedTrend: number;
    redeemedTrend: number;
  };
}

export interface SmartCoinFlow {
  period: string;
  flowChart: Array<{
    date: string;
    earned: number;
    earnedEuros: number;
    redeemed: number;
    redeemedEuros: number;
    netFlow: number;
    netFlowEuros: number;
  }>;
}

export interface BusinessStats {
  period: string;
  businesses: Array<{
    id: number;
    name: string;
    avatar: string;
    businessType: string;
    verified: boolean;
    orderCount: number;
    revenue: number;
    postCount: number;
  }>;
  totalBusinesses: number;
  activeBusinesses: number;
}

// Cache Entry Interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  period?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RegionalHubAnalyticsService {
  private baseUrl = `${environment.apiUrl}/regional/analytics`;
  
  // Cache subjects for real-time updates
  private overviewCache$ = new BehaviorSubject<RegionalHubOverview | null>(null);
  private orderAnalyticsCache$ = new BehaviorSubject<OrderAnalytics | null>(null);
  private userAnalyticsCache$ = new BehaviorSubject<UserAnalytics | null>(null);
  private postAnalyticsCache$ = new BehaviorSubject<PostAnalytics | null>(null);
  private smartCoinOverviewCache$ = new BehaviorSubject<SmartCoinOverview | null>(null);
  
  public overview$ = this.overviewCache$.asObservable();
  public orderAnalytics$ = this.orderAnalyticsCache$.asObservable();
  public userAnalytics$ = this.userAnalyticsCache$.asObservable();
  public postAnalytics$ = this.postAnalyticsCache$.asObservable();
  public smartCoinOverview$ = this.smartCoinOverviewCache$.asObservable();

  // Cache storage with TTL
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(key: string, period?: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = (now - entry.timestamp) > this.CACHE_TTL;
    
    const periodMatches = !period || entry.period === period;

    return !isExpired && periodMatches;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, period?: string): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      period
    });
  }

  /**
   * Get cache entry
   */
  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? entry.data : null;
  }

  // ========================================
  // OVERVIEW
  // ========================================

  getOverview(period: string = '30d', forceRefresh: boolean = false): Observable<RegionalHubOverview> {
    const cacheKey = `overview_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<RegionalHubOverview>(cacheKey);
      if (cached) {
        this.overviewCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<RegionalHubOverview>(`${this.baseUrl}/overview`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.overviewCache$.next(data);
      })
    );
  }

  // ========================================
  // ORDER ANALYTICS
  // ========================================

  getOrderAnalytics(period: string = '30d', forceRefresh: boolean = false): Observable<OrderAnalytics> {
    const cacheKey = `order_analytics_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<OrderAnalytics>(cacheKey);
      if (cached) {
        this.orderAnalyticsCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<OrderAnalytics>(`${this.baseUrl}/orders/analytics`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.orderAnalyticsCache$.next(data);
      })
    );
  }

  getOrderMetrics(period: string = '30d', granularity?: string, forceRefresh: boolean = false): Observable<OrderMetrics> {
    const cacheKey = `order_metrics_${period}_${granularity || 'auto'}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<OrderMetrics>(cacheKey);
      if (cached) return of(cached);
    }

    let params = new HttpParams().set('period', period);
    if (granularity) {
      params = params.set('granularity', granularity);
    }
    
    return this.http.get<OrderMetrics>(`${this.baseUrl}/orders/metrics`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // USER ANALYTICS
  // ========================================

  getUserAnalytics(period: string = '30d', forceRefresh: boolean = false): Observable<UserAnalytics> {
    const cacheKey = `user_analytics_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<UserAnalytics>(cacheKey);
      if (cached) {
        this.userAnalyticsCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<UserAnalytics>(`${this.baseUrl}/users/analytics`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.userAnalyticsCache$.next(data);
      })
    );
  }

  getUserGrowth(period: string = '30d', forceRefresh: boolean = false): Observable<UserGrowth> {
    const cacheKey = `user_growth_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<UserGrowth>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<UserGrowth>(`${this.baseUrl}/users/growth`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // POST ANALYTICS
  // ========================================

  getPostAnalytics(period: string = '30d', forceRefresh: boolean = false): Observable<PostAnalytics> {
    const cacheKey = `post_analytics_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<PostAnalytics>(cacheKey);
      if (cached) {
        this.postAnalyticsCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<PostAnalytics>(`${this.baseUrl}/posts/analytics`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.postAnalyticsCache$.next(data);
      })
    );
  }

  getPostEngagement(period: string = '30d', forceRefresh: boolean = false): Observable<PostEngagement> {
    const cacheKey = `post_engagement_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<PostEngagement>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<PostEngagement>(`${this.baseUrl}/posts/engagement`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // SMARTCOIN ANALYTICS
  // ========================================

  getSmartCoinOverview(period: string = '30d', forceRefresh: boolean = false): Observable<SmartCoinOverview> {
    const cacheKey = `smartcoin_overview_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<SmartCoinOverview>(cacheKey);
      if (cached) {
        this.smartCoinOverviewCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<SmartCoinOverview>(`${this.baseUrl}/smartcoins/overview`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.smartCoinOverviewCache$.next(data);
      })
    );
  }

  getSmartCoinFlow(period: string = '30d', forceRefresh: boolean = false): Observable<SmartCoinFlow> {
    const cacheKey = `smartcoin_flow_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<SmartCoinFlow>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<SmartCoinFlow>(`${this.baseUrl}/smartcoins/flow`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // BUSINESS STATS
  // ========================================

  getBusinessStats(period: string = '30d', forceRefresh: boolean = false): Observable<BusinessStats> {
    const cacheKey = `business_stats_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<BusinessStats>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<BusinessStats>(`${this.baseUrl}/businesses/stats`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh all analytics data
   */
  refreshAllData(period: string = '30d'): void {
    this.getOverview(period, true).subscribe();
    this.getOrderAnalytics(period, true).subscribe();
    this.getUserAnalytics(period, true).subscribe();
    this.getPostAnalytics(period, true).subscribe();
    this.getSmartCoinOverview(period, true).subscribe();
  }

  /**
   * Invalidate specific cache entry
   */
  invalidateCache(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate cache for specific period
   */
  invalidatePeriod(period: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      if (key.includes(period)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.overviewCache$.next(null);
    this.orderAnalyticsCache$.next(null);
    this.userAnalyticsCache$.next(null);
    this.postAnalyticsCache$.next(null);
    this.smartCoinOverviewCache$.next(null);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; entries: string[] } {
    const entries: string[] = [];
    this.cache.forEach((value, key) => {
      const age = Date.now() - value.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      entries.push(`${key} (${ageMinutes}m old)`);
    });

    return {
      size: this.cache.size,
      entries
    };
  }
}